import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CheckCircle as CompleteIcon,
  Description as NotesIcon,
  Psychology as PsychologyIcon,
  Close as CloseIcon,
  AutoAwesome as MagicIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import * as supervisorApi from '../../services/supervisorApi';
import api, { getPlatformSettings } from '../../services/api';
import { useSocket } from '../../context/SocketContext';
import { useTherapistUiContext, useTherapistUiContextRegistration } from '../../context/TherapistUiContext';
import SupportTicketDialog from '../chat/SupportTicketDialog';
import SupervisorChatInterface from './SupervisorChatInterface';
import CareNotesManager from './CareNotesManager';
import ConsultationNotesDisplay from './ConsultationNotesDisplay';
import ConsultationSummaryReview from './ConsultationSummaryReview';

function SupervisorConsultationView({ token }) {
  const { consultationId, clientId, conversationId } = useParams();
  const navigate = useNavigate();
  const socket = useSocket();
  
  // State
  const [consultation, setConsultation] = useState(null);
  const [client, setClient] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pendingSuggestedMessage, setPendingSuggestedMessage] = useState('');

  // Help / Report-an-issue. This page lives under /supervisor/*, outside the
  // therapist TopBar shell, so it mounts its own help button and dialog. The
  // consultation, client and conversation ids are registered so the ticket
  // context matches what the TopBar would have captured.
  const [supportTicketOpen, setSupportTicketOpen] = useState(false);
  const { getReportContext } = useTherapistUiContext();
  useTherapistUiContextRegistration('consultation', {
    consultationId: consultation?._id || consultationId || undefined,
    clientId: client?._id || clientId || undefined,
    conversationId: conversation?._id || conversationId || undefined,
  });
  const reportContext = supportTicketOpen ? getReportContext() : null;

  // Dialogs
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showCareNotesDialog, setShowCareNotesDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showSummaryReview, setShowSummaryReview] = useState(false);
  const [canSaveNotes, setCanSaveNotes] = useState(false);
  const [showCompletedNotes, setShowCompletedNotes] = useState(true);
  
  // Notes state
  const [notesSummary, setNotesSummary] = useState('');
  const [keyInsights, setKeyInsights] = useState(['']);
  const [recommendations, setRecommendations] = useState(['']);
  const [aiSuggestedSummary, setAiSuggestedSummary] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  
  // Refs
  const chatInterfaceRef = useRef();

  // Initialize or load consultation
  useEffect(() => {
    let isMounted = true; // Add cleanup flag
    
    const initializeConsultation = async () => {
      if (!isMounted) return; // Check if component is still mounted
      try {
        setLoading(true);
        
        if (consultationId) {
          // Load existing consultation
          const { consultation: loadedConsultation } = await supervisorApi.getConsultation(consultationId, token);
          setConsultation(loadedConsultation);
          setClient(loadedConsultation.clientId);
          setConversation(loadedConsultation.conversationId);
          
          // Load notes if they exist
          if (loadedConsultation.consultationNotes?.summary) {
            setNotesSummary(loadedConsultation.consultationNotes.summary);
            setKeyInsights(loadedConsultation.consultationNotes.keyInsights || ['']);
            setRecommendations(loadedConsultation.consultationNotes.recommendations || ['']);
          }
        } else if (clientId && conversationId) {
          // Start new consultation
          const [clientData, conversationData] = await Promise.all([
            api.get(`/therapist/clients/${clientId}`),
            api.get(`/therapist/conversations/${conversationId}`)
          ]);
          
          setClient(clientData.data);
          setConversation(conversationData.data.conversation);
          
          // Prepare initial context message
          const suggestedMessage = `I need consultation about my client ${clientData.data.firstName || 'this client'}. 
I'd like to discuss their progress and treatment approach in this conversation.

Could you help me analyze:
- Their current emotional state and progress
- Any concerning patterns in our recent sessions
- Recommendations for therapeutic interventions
- Whether my current approach is appropriate`;
          
          // Start consultation (doesn't send message automatically anymore)
          const result = await supervisorApi.startConsultation(clientId, conversationId, suggestedMessage, token);
          setConsultation(result.consultation);
          
          // Store the suggested message to be set after component renders
          setPendingSuggestedMessage(result.suggestedMessage || suggestedMessage);
          
          navigate(`/supervisor/consultation/${result.consultation._id}`, { replace: true });
        } else {
          setError('Invalid consultation parameters');
        }
      } catch (err) {
        console.error('Error initializing consultation:', err);
        
        // Check if there's already an active consultation
        if (err.response?.status === 400 && err.response?.data?.consultationId) {
          // Redirect to the existing consultation
          navigate(`/supervisor/consultation/${err.response.data.consultationId}`, { replace: true });
          return;
        }
        
        setError(err.response?.data?.msg || 'Failed to initialize consultation');
      } finally {
        setLoading(false);
      }
    };

    initializeConsultation();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [consultationId, clientId, conversationId, navigate]);

  // Load user and platform settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Get user info - using the correct endpoint
        const userResponse = await api.get('/users/me');
        setUserSettings(userResponse.data);
        console.log('User settings loaded:', userResponse.data);
        
        // Get platform settings
        const settings = await getPlatformSettings(token);
        setPlatformSettings(settings);
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    
    if (token) {
      loadSettings();
    }
  }, [token]);

  // Set the initial message once the consultation and chat interface are ready
  useEffect(() => {
    if (pendingSuggestedMessage && chatInterfaceRef.current && consultation && consultation.messages.length === 0) {
      chatInterfaceRef.current.setInitialMessage(pendingSuggestedMessage);
      setPendingSuggestedMessage(''); // Clear it after setting
    }
  }, [pendingSuggestedMessage, consultation]);

  // Socket event handlers - memoized to prevent recreating on every render
  const handleNewMessage = useCallback((data) => {
    if (data.consultationId === consultation?._id) {
      // Don't add messages here - they're already added via the API response
      // This prevents duplicate messages in the chat
      console.log('Socket message received but not adding (handled by API response)');
    }
  }, [consultation?._id]);

  const handleTypingIndicator = useCallback((data) => {
    if (data.consultationId === consultation?._id && chatInterfaceRef.current) {
      chatInterfaceRef.current.setTypingIndicator(data.isTyping);
    }
  }, [consultation?._id]);

  // Handle AI-generated summary suggestion
  const handleSummarySuggestion = useCallback((data) => {
    if (data.consultationId === consultation?._id) {
      setAiSuggestedSummary(data.summaryData);
      setShowSummaryReview(true);
    }
  }, [consultation?._id]);

  const handleConsultationEnded = useCallback((data) => {
    if (data.consultationId === consultation?._id) {
      setConsultation(prev => ({ ...prev, status: 'completed', completedAt: data.completedAt }));
    }
  }, [consultation?._id]);

  // Socket.IO connection for real-time updates
  useEffect(() => {
    if (socket && consultation?._id) {
      // Join consultation room
      socket.emit('therapist:join_consultation_room', consultation._id);
      
      // Listen for real-time events
      socket.on('ai_supervisor:new_message', handleNewMessage);
      socket.on('ai_supervisor:typing', handleTypingIndicator);
      socket.on('ai_supervisor:consultation_ended', handleConsultationEnded);
      socket.on('consultation_summary_suggestion', handleSummarySuggestion);
      
      return () => {
        socket.emit('therapist:leave_consultation_room', consultation._id);
        socket.off('ai_supervisor:new_message', handleNewMessage);
        socket.off('ai_supervisor:typing', handleTypingIndicator);
        socket.off('ai_supervisor:consultation_ended', handleConsultationEnded);
        socket.off('consultation_summary_suggestion', handleSummarySuggestion);
      };
    }
  }, [socket, consultation?._id, handleNewMessage, handleTypingIndicator, handleConsultationEnded, handleSummarySuggestion]);

  // Refs for dialog inputs to prevent re-renders on typing
  const dialogFormRef = useRef({
    summary: '',
    insights: [''],
    recommendations: ['']
  });

  // Update ref and canSave state when dialog opens
  useEffect(() => {
    if (showNotesDialog) {
      // Initialize the ref with current state values
      dialogFormRef.current = {
        summary: notesSummary || '',
        insights: keyInsights.length > 0 ? [...keyInsights] : [''],
        recommendations: recommendations.length > 0 ? [...recommendations] : ['']
      };
      // Enable save if there's already a summary
      setCanSaveNotes((notesSummary || '').trim().length > 0);
    }
  }, [showNotesDialog, notesSummary, keyInsights, recommendations]);

  // Save consultation notes
  const handleSaveNotes = async () => {
    try {
      setIsSaving(true);
      // Get values from dialogFormRef
      const summary = dialogFormRef.current.summary;
      const validInsights = dialogFormRef.current.insights.filter(i => i.trim());
      const validRecommendations = dialogFormRef.current.recommendations.filter(r => r.trim());
      
      
      await supervisorApi.saveConsultationNotes(
        consultation._id,
        summary,
        validInsights,
        validRecommendations,
        false, // wasEdited - false for manual notes
        token
      );
      
      setShowNotesDialog(false);
      // Update state with saved values
      setNotesSummary(summary);
      setKeyInsights(validInsights.length > 0 ? validInsights : ['']);
      setRecommendations(validRecommendations.length > 0 ? validRecommendations : ['']);
      
      // Update local consultation state
      setConsultation(prev => ({
        ...prev,
        consultationNotes: {
          summary: summary,
          keyInsights: validInsights,
          recommendations: validRecommendations,
          savedAt: new Date()
        }
      }));
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Failed to save consultation notes');
    } finally {
      setIsSaving(false);
    }
  };

  // Complete consultation
  const handleCompleteConsultation = async () => {
    try {
      setIsSaving(true);
      const result = await supervisorApi.completeConsultation(consultation._id, token);
      setConsultation(result.consultation);
      setShowCompleteDialog(false);
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/therapist/dashboard', { state: { activeTab: 2 } }); // Consultations tab is index 2
      }, 1500);
    } catch (err) {
      console.error('Error completing consultation:', err);
      setError('Failed to complete consultation');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle care note creation
  const handleCareNoteCreated = useCallback((careNote) => {
    setConsultation(prev => ({
      ...prev,
      careNotes: [...(prev.careNotes || []), careNote]
    }));
    setShowCareNotesDialog(false);
  }, []);


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const isCompleted = consultation?.status === 'completed';

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 2, borderRadius: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <PsychologyIcon color="primary" />
            <Box>
              <Typography variant="h6">
                AI Supervisor Consultation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Client: {client?.firstName} {client?.lastName} • 
                {isCompleted ? ' Completed' : ' Active'}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" gap={1}>
            {!isCompleted && (
              <>
                <Tooltip title="Generate AI summary">
                  <IconButton 
                    onClick={() => {
                      // Trigger summary generation by sending a message to supervisor
                      if (chatInterfaceRef.current) {
                        chatInterfaceRef.current.sendMessage(
                          "Please generate a comprehensive consultation summary with care note suggestions using the generate_consultation_summary function."
                        );
                      }
                    }} 
                    color="secondary"
                  >
                    <MagicIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Save consultation notes">
                  <IconButton onClick={() => setShowNotesDialog(true)} color="primary">
                    <NotesIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CompleteIcon />}
                  onClick={() => setShowCompleteDialog(true)}
                >
                  Complete
                </Button>
              </>
            )}
            {isCompleted && (
              <Box display="flex" gap={1}>
                {consultation.consultationNotes?.summary && (
                  <Chip 
                    label="Notes Saved" 
                    color="success" 
                    variant={showCompletedNotes ? "filled" : "outlined"}
                    icon={<SaveIcon />}
                    onClick={() => setShowCompletedNotes(!showCompletedNotes)}
                    sx={{ cursor: 'pointer' }}
                  />
                )}
                {consultation.careNotes?.length > 0 && (
                  <Chip
                    label={`${consultation.careNotes.length} Care Notes`}
                    color="secondary"
                    variant={showCompletedNotes ? "filled" : "outlined"}
                    onClick={() => setShowCompletedNotes(!showCompletedNotes)}
                    sx={{ cursor: 'pointer' }}
                  />
                )}
              </Box>
            )}
            <Tooltip title="Get help / report an issue">
              <IconButton onClick={() => setSupportTicketOpen(true)} data-testid="therapist-help-button">
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <SupervisorChatInterface
          ref={chatInterfaceRef}
          consultation={consultation}
          disabled={isCompleted}
          onCreateCareNote={() => setShowCareNotesDialog(true)}
          token={token}
          userSettings={userSettings}
        />
      </Box>

      {/* Save Notes Dialog */}
      <Dialog 
        open={showNotesDialog} 
        onClose={() => setShowNotesDialog(false)}
        maxWidth="md"
        fullWidth
        key={showNotesDialog ? 'notes-dialog-open' : 'notes-dialog-closed'}
      >
        <DialogTitle>
          Save Consultation Notes
          <IconButton
            onClick={() => setShowNotesDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Summary"
            defaultValue={notesSummary}
            onChange={(e) => { 
              dialogFormRef.current.summary = e.target.value;
              setCanSaveNotes(e.target.value.trim().length > 0);
            }}
            margin="normal"
            helperText="Summarize the key points discussed in this consultation"
          />
          
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Key Insights
          </Typography>
          {keyInsights.map((insight, index) => (
            <Box key={`insight-${index}`} display="flex" gap={1} mb={1}>
              <TextField
                fullWidth
                size="small"
                defaultValue={insight}
                onChange={(e) => { dialogFormRef.current.insights[index] = e.target.value; }}
                placeholder="Enter a key insight..."
              />
              {keyInsights.length > 1 && (
                <IconButton 
                  size="small"
                  onClick={() => {
                    dialogFormRef.current.insights = dialogFormRef.current.insights.filter((_, i) => i !== index);
                    setKeyInsights([...dialogFormRef.current.insights]);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Button 
            size="small" 
            onClick={() => {
              dialogFormRef.current.insights.push('');
              setKeyInsights([...dialogFormRef.current.insights]);
            }}
          >
            Add Insight
          </Button>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Recommendations
          </Typography>
          {recommendations.map((rec, index) => (
            <Box key={`rec-${index}`} display="flex" gap={1} mb={1}>
              <TextField
                fullWidth
                size="small"
                defaultValue={rec}
                onChange={(e) => { dialogFormRef.current.recommendations[index] = e.target.value; }}
                placeholder="Enter a recommendation..."
              />
              {recommendations.length > 1 && (
                <IconButton 
                  size="small"
                  onClick={() => {
                    dialogFormRef.current.recommendations = dialogFormRef.current.recommendations.filter((_, i) => i !== index);
                    setRecommendations([...dialogFormRef.current.recommendations]);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Button 
            size="small" 
            onClick={() => {
              dialogFormRef.current.recommendations.push('');
              setRecommendations([...dialogFormRef.current.recommendations]);
            }}
          >
            Add Recommendation
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowNotesDialog(false);
            setCanSaveNotes(false);
            // Reset dialog form ref
            dialogFormRef.current = {
              summary: notesSummary,
              insights: [...keyInsights],
              recommendations: [...recommendations]
            };
          }}>Cancel</Button>
          <Button 
            onClick={handleSaveNotes} 
            variant="contained"
            disabled={isSaving || !canSaveNotes}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Save Notes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Care Notes Dialog */}
      <CareNotesManager
        open={showCareNotesDialog}
        onClose={() => setShowCareNotesDialog(false)}
        consultationId={consultation?._id}
        onCareNoteCreated={handleCareNoteCreated}
        token={token}
      />

      {/* Complete Consultation Dialog */}
      <Dialog open={showCompleteDialog} onClose={() => setShowCompleteDialog(false)}>
        <DialogTitle>Complete Consultation?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to complete this consultation? 
            You won't be able to send more messages after completion.
          </Typography>
          {!consultation?.consultationNotes?.summary && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              You haven't saved any consultation notes yet. Consider saving notes before completing.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCompleteConsultation}
            variant="contained"
            color="success"
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Complete Consultation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Consultation Notes Display Dialog */}
      <Dialog 
        open={isCompleted && showCompletedNotes} 
        onClose={() => setShowCompletedNotes(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Consultation Summary
          <IconButton
            onClick={() => setShowCompletedNotes(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {consultation && <ConsultationNotesDisplay consultation={consultation} />}
        </DialogContent>
      </Dialog>

      {/* AI-Generated Summary Review Dialog */}
      <ConsultationSummaryReview
        open={showSummaryReview}
        onClose={() => setShowSummaryReview(false)}
        summaryData={aiSuggestedSummary}
        consultationId={consultation?._id}
        token={token}
        onSaved={(data) => {
          // Update local state with saved data
          if (data.summary) {
            setNotesSummary(data.summary.summary);
            setKeyInsights(data.summary.keyInsights);
            setRecommendations(data.summary.recommendations);
          }
          setShowSummaryReview(false);
          // Show a success message or refresh consultation
        }}
      />

      <SupportTicketDialog
        open={supportTicketOpen}
        onClose={() => setSupportTicketOpen(false)}
        token={token}
        currentPage={reportContext?.currentPage || 'therapist_consultation'}
        extraContext={reportContext || { userRole: 'therapist' }}
        reportingFrom={reportContext?.uiLocation}
      />
    </Box>
  );
}

export default SupervisorConsultationView;